import { useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { authClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';

/** Classify signInWithPassword errors: prefer HTTP status, fall back to message text */
function classifyCredentialError(err: unknown): string {
  const status = err != null && typeof err === 'object' && 'status' in err
    ? (err as { status: unknown }).status
    : undefined;
  if (status === 429) return 'Too many attempts — please wait a moment and try again';
  if (status === 400 || status === 401) return 'Current password is incorrect';
  // Non-status errors: check message for network issues
  const msg = (err instanceof Error ? err.message : '').toLowerCase();
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('timeout')) {
    return 'Network error — check your connection and try again';
  }
  return 'Something went wrong — please try again';
}

export function ChangePasswordForm() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Verify current password
      const { error: signInError } = await authClient.auth.signInWithPassword({
        email: user?.email ?? '',
        password: currentPassword,
      });

      if (signInError) {
        setError(classifyCredentialError(signInError));
        setIsSubmitting(false);
        return;
      }

      // Step 2: Update password
      const { error: updateError } = await authClient.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message || 'Failed to update password');
        setIsSubmitting(false);
        return;
      }

      // Step 3: Fire password_changed event (fire-and-forget)
      // Get fresh session after updateUser — old token may be rotated.
      // Use raw fetch to bypass apiFetch's global 401 sign-out side effect.
      void authClient.auth.getSession().then(({ data: { session: freshSession } }) => {
        if (!freshSession?.access_token) {
          console.warn('[password-change] no fresh session after updateUser, skipping tracking');
          return;
        }
        const url = `${import.meta.env.VITE_API_URL || '/api'}/user/password-changed`;
        fetch(url, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${freshSession.access_token}` },
        }).catch(() => {});
      }).catch(() => {});

      // Success
      toast.success('Password changed successfully');
      resetForm();
      setIsOpen(false);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <div>
          <div className="text-white font-medium">Change Password</div>
          <div className="text-sm text-slate-400">Update your account password</div>
        </div>
        <button
          onClick={() => setIsOpen(true)}
          className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="pt-4 border-t border-white/[0.06]">
      <div className="text-white font-medium mb-3">Change Password</div>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Current Password */}
        <div>
          <label htmlFor="currentPassword" className="block text-sm text-slate-400 mb-1">
            Current Password
          </label>
          <div className="relative">
            <input
              id="currentPassword"
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              className="w-full px-4 py-2.5 bg-[#111111] border border-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 pr-10"
              required
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
            >
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label htmlFor="newPassword" className="block text-sm text-slate-400 mb-1">
            New Password
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="w-full px-4 py-2.5 bg-[#111111] border border-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 pr-10"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmNewPassword" className="block text-sm text-slate-400 mb-1">
            Confirm New Password
          </label>
          <div className="relative">
            <input
              id="confirmNewPassword"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-4 py-2.5 bg-[#111111] border border-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 pr-10"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => { resetForm(); setIsOpen(false); }}
            disabled={isSubmitting}
            className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Updating...
              </>
            ) : (
              'Update Password'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
